import { useQueries } from '@tanstack/react-query';

import ReservationService from '~/services/ReservationService';

export const surchargeMarginQueryKeys = {
  all: ['surchargeMargin'] as const,
  stay: (roomRateId: string | undefined, startDate: string, endDate: string) =>
    [...surchargeMarginQueryKeys.all, roomRateId, startDate, endDate] as const,
};

/** AUD surcharge margin for each stay on one room rate, keyed by its first night. */
export function useGetSurchargeMargins(
  roomRateId: string | undefined,
  stays: Array<{ startDate: string; endDate: string }>,
) {
  return useQueries({
    queries: stays.map(({ startDate, endDate }) => ({
      queryKey: surchargeMarginQueryKeys.stay(roomRateId, startDate, endDate),
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const response = await ReservationService.getSurchargeMargin(roomRateId, { startDate, endDate }, { signal });
        return response.result?.[0]?.marginAud ?? 0;
      },
      enabled: !!roomRateId,
      staleTime: 5 * 60 * 1000,
    })),
    combine: (results) => ({
      byStartDate: Object.fromEntries(
        stays.flatMap((stay, index) =>
          results[index]?.data === undefined ? [] : [[stay.startDate, results[index].data] as const],
        ),
      ),
      isLoading: results.some((result) => result.isLoading),
      error: results.find((result) => result.error)?.error,
    }),
  });
}
